import { Badge, Flex, IconButton, Text } from "@radix-ui/themes";
import { ToolInvocation, ToolResult } from "ai";
import styles from "./styles.module.scss";
import { ReactElement, useCallback, useRef, useState } from "react";
import { CodeIcon, CheckIcon, CopyIcon } from "@radix-ui/react-icons";

import cx from "classnames";
import { Brain, AlertTriangle } from "lucide-react";
import { IoSparkles } from "react-icons/io5";

export function AgentWrapper({
  children,
  hideIcon = false,
  actionIcon = false,
  isEscalation = false,
}: {
  children: ReactElement;
  hideIcon?: boolean;
  actionIcon?: boolean;
  isEscalation?: boolean;
}) {
  return (
    <div className={styles.assistantWrapper}>
      {actionIcon && (
        <div className={styles.actionIcon}>
          <Brain />
        </div>
      )}
      {isEscalation && (
        <div className={styles.escalationIcon}>
          <AlertTriangle />
        </div>
      )}
      {!hideIcon && (
        <div className={styles.sparklesIcon}>
          <IoSparkles />
        </div>
      )}
      {children}
    </div>
  );
}

export function ToolViewer({
  tool,
  isEscalation = false,
  isResolved = false,
}: {
  tool: ToolInvocation & ToolResult<any, any, any>;
  isEscalation?: boolean;
  isResolved?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (isEscalation) {
    return (
      <Flex
        direction={"column"}
        className={cx(
          styles.message,
          styles["escalation"],
          "  flex-col gap-2 w-full"
        )}
      >
        <Text className=" text-sm">
          Quack will escalate it due to: <Badge>{tool.args.reason}</Badge>
        </Text>
        <Text className=" text-xs">Reason: {tool.args.explanation}</Text>
        {tool.args.quote && (
          <Text className="text-xs italic text-gray-600">
            Quote: &quot;{tool.args.quote}&quot;
          </Text>
        )}
      </Flex>
    );
  }

  if (isResolved) {
    return (
      <Flex
        direction={"column"}
        className={cx(
          styles.message,
          styles["resolved"],
          " flex-col gap-2 w-full"
        )}
      >
        <Text className=" text-sm">Quack has resolved the ticket</Text>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        className={cx(
          styles.message,
          styles["action"],
          " justify-between flex-col gap-2 w-full"
        )}
      >
        <div
          className={"flex justify-between w-full"}
          style={{
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          {tool?.result?.data?.activity ?? tool?.toolName}
          <IconButton
            color="gray"
            variant="ghost"
            size="1"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <CodeIcon />
          </IconButton>
        </div>
        {isExpanded ? (
          <>
            {tool?.result?.data?.curlResponse || tool?.args ? (
              <Flex className="flex-col gap-2 w-full border-t pt-2">
                {tool?.args ? (
                  <PreViewer title="Request Arguments">
                    <>
                      {JSON.stringify(
                        Object.fromEntries(
                          Object.entries(tool.args || {}).filter(
                            ([key]) => key !== "activity"
                          )
                        ),
                        null,
                        2
                      )}
                    </>
                  </PreViewer>
                ) : null}
                {tool?.result?.data?.curlResponse ? (
                  <PreViewer title="Response">
                    <>
                      {JSON.stringify(
                        tool?.result?.data?.curlResponse,
                        null,
                        2
                      )}
                    </>
                  </PreViewer>
                ) : null}
              </Flex>
            ) : (
              <></>
            )}
          </>
        ) : null}
      </Flex>
    </>
  );
}

function PreViewer({
  children,
  title,
}: {
  children: ReactElement;
  title: string;
}) {
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (preRef.current) {
      const text = preRef.current.innerText;
      navigator.clipboard.writeText(text).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
          // log copy event if needed
        },
        (err) => {
          // log error if needed
        }
      );
    }
  }, []);

  return (
    <Flex className="flex-col gap-2">
      <Text>{title}</Text>
      <Flex align="center" gap="2" className="w-full relative">
        <pre
          ref={preRef}
          className=" bg-sky-50 m-0 w-full text-xs border-sky-200 "
          style={{ flex: 1 }}
        >
          {children}
        </pre>
        <IconButton
          size="1"
          color={copied ? "green" : "gray"}
          variant="ghost"
          onClick={handleCopy}
          className="absolute  top-4 right-4"
          aria-label="Copy to clipboard"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </IconButton>{" "}
      </Flex>
    </Flex>
  );
}
